import { Tab, Spinner, SpinnerSize } from "@blueprintjs/core";
import { useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { DogApi, EIsagNorm } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { BackButton, PageBase, ProtectedTabs } from "../../../../components";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DogGenealogyPage } from "./DogGenealogyPage";
import { DogLinkedFilesPage } from "./Requests/DogLinkedFilesPage";
import { DogDysplasiaList } from "./health/DogDysplasiaList";
import { DogWorkbookListPage } from "./Workbook/DogWorkbookListPage";
import { DogCompetitionPage } from "./competition/DogCompetitionPage";
import { DogHealthTestList } from "./health/DogHealthTestList";
import { DogDnaItemPage } from "./health/DogDnaItemPage";
import { DogDetailPage } from "./DogDetails/DogDetailPage";
import { DogLinkedBroodRequests } from "./Requests/DogLinkedBroodRequests";
import { DogHistoryTab } from "./DogHistoryTab";
import { CoupleInbreedingPanel } from "./CoupleInbreedingPanel";
import { CoupleInBreedingList } from "./CoupleInBreedingList";

export interface IDogItemPageProps {}

export const DogItemPage: React.FunctionComponent<IDogItemPageProps> = (props) => {
  const { id, tab, state } = useParams<{ id: string; tab: string; state: string }>();
  const history = useHistory();
  const api = useApiService(DogApi);
  const [displayName, loading] = useApiEffect(() => api.getDisplayName(+id), [id]);
  const { t } = useTl();

  const dogId = React.useMemo(() => +id, [id]);

  return (
    <PageBase
      breadCrumbs={[
        { text: t(ETLCodes.Dogs), route: ERoutes.dog },
        { text: +id > 0 ? loading ? <Spinner size={SpinnerSize.SMALL} /> : displayName : t(ETLCodes.New) },
      ]}
      actionHistory={+id > 0 && { tlCode: ETLCodes.ActionHistoryDog, name: displayName, id: +id }}
    >
      <ProtectedTabs
        id="TabsColor"
        onChange={(newTabId) => history.push(`${ERoutes.dog}/${dogId}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.dog} />
        <Tab
          id="detail"
          title={t(ETLCodes.Detail)}
          panel={<DogDetailPage dogId={dogId} editMode={state === "edit"} />}
        />
        <Tab
          id="linkedBroodFiles"
          title={t(ETLCodes.LinkedBroodRequests)}
          panel={<DogLinkedBroodRequests dogId={+id} />}
          disabled={dogId <= 0}
        />
        <Tab
          id="coupleInbreeding"
          title={t(ETLCodes.CoupleInbreeding)}
          panel={
            <>
              <CoupleInBreedingList dogId={+id} />
              <CoupleInbreedingPanel dogId={+id} />
            </>
          }
          disabled={dogId <= 0}
        />
        <Tab
          id="linkedFiles"
          title={t(ETLCodes.LinkedRequests)}
          panel={<DogLinkedFilesPage dogId={dogId} />}
          disabled={dogId <= 0}
        />
        <Tab
          id="sport"
          title={t(ETLCodes.Sport)}
          panel={<DogCompetitionPage editMode={state === "edit"} dogId={dogId} />}
          disabled={dogId <= 0}
        />
        <Tab
          id="workbooks"
          title={t(ETLCodes.Workbooks)}
          panel={<DogWorkbookListPage dogId={dogId} editMode={state === "edit"} />}
          disabled={dogId <= 0}
        />
        <Tab
          id="dysplasia"
          title={t(ETLCodes.Dysplasia)}
          panel={<DogDysplasiaList dogId={dogId} />}
          disabled={dogId <= 0}
        />
        <Tab
          id="healthTests"
          title={t(ETLCodes.HealthTests)}
          panel={<DogHealthTestList dogId={dogId} editMode={state === "edit"} />}
          disabled={dogId <= 0}
        />
        <Tab
          id="dna2006"
          title={t(ETLCodes.DnaTestIsag2006)}
          panel={<DogDnaItemPage dogId={dogId} editMode={state === "edit"} isag={EIsagNorm.Norm2006} />}
          disabled={dogId <= 0}
        />
        <Tab
          id="dna2020"
          title={t(ETLCodes.DnaTestIsag20)}
          panel={<DogDnaItemPage dogId={dogId} editMode={state === "edit"} isag={EIsagNorm.Norm2020} />}
          disabled={dogId <= 0}
        />
        <Tab
          id="genealogy"
          title={t(ETLCodes.Genealogy)}
          panel={<DogGenealogyPage dogId={dogId} />}
          disabled={dogId <= 0}
        />
        <Tab id="history" title={t(ETLCodes.History)} panel={<DogHistoryTab dogId={dogId} />} disabled={dogId <= 0} />
      </ProtectedTabs>
    </PageBase>
  );
};
